import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  isTerminalDocumentStatus,
  listSelaDocuments,
  type SelaDocument,
  type SelaDocumentStatus,
  type SelaProjectDetail,
} from '../../services/selaApi';
import { uploadSelaPdfDocument } from './SelaDocumentUploadButton';
import { useAskSela } from './ask-sela/AskSelaContext';

type WorkspaceContext = {
  project: SelaProjectDetail;
};

const POLL_INTERVAL_MS = 4000;
const MAX_PDF_BYTES = 50 * 1024 * 1024;

const STATUS_LABEL: Record<SelaDocumentStatus, string> = {
  uploaded: 'Uploaded',
  textract: 'Running OCR',
  rendering: 'Rendering pages',
  extracting: 'Extracting with AI',
  validating: 'Validating',
  writing: 'Writing graph',
  extracted: 'Ready',
  failed: 'Failed',
};

const STATUS_TONE: Record<SelaDocumentStatus, string> = {
  uploaded: 'bg-slate-100 text-slate-700',
  textract: 'bg-blue-50 text-blue-700',
  rendering: 'bg-blue-50 text-blue-700',
  extracting: 'bg-indigo-50 text-indigo-700',
  validating: 'bg-indigo-50 text-indigo-700',
  writing: 'bg-indigo-50 text-indigo-700',
  extracted: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
};

function formatBytes(size: number | undefined): string {
  if (!size || Number.isNaN(size)) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(value?: string): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function attachedActionLabel(doc: SelaDocument): string {
  if (doc.deliverableId || doc.requirementId) {
    return doc.uploadedFor || doc.uploadReason || doc.deliverableId || doc.requirementId || 'Roadmap action';
  }
  if (doc.blockerId) {
    return doc.uploadedFor || doc.blockerId;
  }
  return doc.workflowContext === 'library' ? 'Project library' : doc.workflowContext || 'Project library';
}

export function SelaProjectDocumentsPage() {
  const { project } = useOutletContext<WorkspaceContext>();
  const { openAskSela } = useAskSela();
  const projectId = project.id;
  const [documents, setDocuments] = useState<SelaDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reload = useCallback(
    async (showSpinner: boolean) => {
      try {
        if (showSpinner) {
          setLoading(true);
          setLoadError(null);
        }
        const next = await listSelaDocuments(projectId);
        setDocuments(next);
      } catch (err) {
        if (showSpinner) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load documents.');
        }
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    void reload(true);
  }, [reload]);

  const hasInFlight = useMemo(
    () => documents.some((doc) => !isTerminalDocumentStatus(doc.processingStatus)),
    [documents],
  );

  useEffect(() => {
    if (!hasInFlight) return;
    const id = window.setInterval(() => {
      void reload(false);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [hasInFlight, reload]);

  const handleFiles = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files || (Array.isArray(files) ? files.length === 0 : files.length === 0)) {
        return;
      }
      const file = Array.isArray(files) ? files[0] : files[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setUploadError('Only .pdf files are supported.');
        return;
      }
      if (file.size > MAX_PDF_BYTES) {
        setUploadError(`File exceeds the ${MAX_PDF_BYTES / (1024 * 1024)} MB limit.`);
        return;
      }
      setUploadError(null);
      setUploading(true);
      try {
        await uploadSelaPdfDocument(projectId, file, {
          workflowContext: 'library',
          uploadReason: 'Project document library upload',
          uploadedFor: 'Document library',
        });
        await reload(false);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      } finally {
        setUploading(false);
      }
    },
    [projectId, reload],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      void handleFiles(event.dataTransfer?.files ?? null);
    },
    [handleFiles],
  );

  return (
    <main className="flex-1 overflow-auto p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">Documents</h1>
        <p className="mt-2 text-sm text-[#647488]">
          Uploaded documents are attached to roadmap actions and analyzed for insights. Open the{' '}
          <Link to="../timeline" className="font-medium text-[#3251b7] hover:underline">
            Roadmap
          </Link>{' '}
          to upload evidence against the action it helps resolve.
        </p>
      </div>

      <label
        htmlFor="sela-doc-upload"
        onDragEnter={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        className={[
          'mb-6 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed bg-white px-6 py-10 text-center transition-colors',
          dragActive
            ? 'border-[#3251b7] bg-[#eff2fe]'
            : 'border-[#cdd5e1] hover:border-[#3251b7] hover:bg-[#f7fafd]',
        ].join(' ')}
      >
        <span className="text-base font-semibold text-[#1e4474]">
          {uploading ? 'Uploading...' : 'Drop a PDF here, or click to choose'}
        </span>
        <span className="mt-1 text-xs text-[#647488]">PDF only, up to 50 MB.</span>
        <input
          id="sela-doc-upload"
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          disabled={uploading}
          onChange={(event) => {
            void handleFiles(event.target.files);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
        />
      </label>

      {uploadError ? (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {uploadError}
        </div>
      ) : null}
      {loadError ? (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {loadError}
        </div>
      ) : null}

      <section className="rounded-xl border border-[#e1e8f0] bg-white shadow-sm">
        <header className="flex items-center justify-between border-b border-[#e7e9ee] px-5 py-3">
          <h2 className="text-sm font-semibold text-[#0f172a]">Documents in this project</h2>
          <span className="text-xs text-[#647488]">
            {documents.length} {documents.length === 1 ? 'document' : 'documents'}
          </span>
        </header>
        {loading ? (
          <div className="px-5 py-6 text-sm text-[#647488]">Loading...</div>
        ) : documents.length === 0 ? (
          <div className="px-5 py-6 text-sm text-[#647488]">
            No documents uploaded yet. Drop a PDF above to get started.
          </div>
        ) : (
          <ul className="divide-y divide-[#e7e9ee]">
            {documents.map((doc) => {
              const statusKey = (doc.processingStatus || 'uploaded') as SelaDocumentStatus;
              return (
                <li key={doc.documentId} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-[#0f172a]">
                        {doc.fileName}
                      </span>
                      <span
                        className={[
                          'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                          STATUS_TONE[statusKey] ?? 'bg-slate-100 text-slate-700',
                        ].join(' ')}
                      >
                        {STATUS_LABEL[statusKey] ?? statusKey}
                      </span>
                      {typeof doc.pageCount === 'number' ? (
                        <span className="text-xs text-[#647488]">
                          {doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-[#647488]">
                      {[formatBytes(doc.fileSize), formatTimestamp(doc.uploadedAt || doc.createdAt)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    <div className="mt-1 text-xs text-[#647488]">
                      <span className="font-medium text-[#475669]">Why:</span>{' '}
                      {doc.uploadReason || doc.uploadedFor || 'No upload reason captured'}
                      {' · '}
                      <span className="font-medium text-[#475669]">Attached action:</span>{' '}
                      {attachedActionLabel(doc)}
                      {doc.gateId ? ` · Stage gate: ${doc.gateId}` : ''}
                    </div>
                    <div className="mt-1 text-xs text-[#647488]">
                      <span className="font-medium text-[#475669]">By:</span>{' '}
                      {doc.uploadedByName || doc.uploadedByEmail || doc.uploadedByUserId || 'Unknown uploader'}
                      {' · '}
                      <span className="font-medium text-[#475669]">When:</span>{' '}
                      {formatTimestamp(doc.uploadedAt || doc.createdAt)}
                    </div>
                    {doc.errorMessage ? (
                      <div className="mt-1 text-xs text-red-600">{doc.errorMessage}</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => openAskSela({
                      type: 'document',
                      label: doc.fileName,
                      sourceId: doc.documentId,
                      sourcePage: 'documents',
                      summary: doc.uploadReason || doc.uploadedFor || '',
                      metadata: {
                        processingStatus: doc.processingStatus,
                        workflowContext: doc.workflowContext,
                        requirementId: doc.requirementId,
                        deliverableId: doc.deliverableId,
                        pageCount: doc.pageCount,
                        errorMessage: doc.errorMessage,
                      },
                    }, isTerminalDocumentStatus(doc.processingStatus)
                      ? `What did "${doc.fileName}" change for this project?`
                      : `Can you analyze "${doc.fileName}" when processing is complete?`)}
                    className="shrink-0 rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7] hover:bg-[#eff2fe]"
                  >
                    Ask Sela
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
